import { create } from 'zustand';
import { BatchedMessage, socketManager, SocketMessage } from '../../../utils/socket-manager';
import { useAuthStore } from '../auth/useAuthStore';
import { refreshAccessToken } from 'features/auth/services/auth-service';
import { ConfigurationSettings } from 'models/configuration-settings';
import { toast } from 'hooks/use-toast';
import { isPathActive } from 'utils/is-path-active';

export const SCHEDULED_MESSAGE_TRIGGERED = 'SCHEDULED_MESSAGE_TRIGGERED';
export const SCHEDULED_JOB_FINISHED = 'SCHEDULED_JOB_FINISHED';

const SCHEDULED_JOB_TOAST_DURATION_MS = 10_000;

type ScheduledMessageTriggeredHandler = (notification: unknown) => void;

let scheduledMessageTriggeredHandler: ScheduledMessageTriggeredHandler | null = null;

/** Chat store registers this so scheduled jobs seed the conversation even when chat.tsx is unmounted. */
export const setScheduledMessageTriggeredHandler = (
  handler: ScheduledMessageTriggeredHandler | null
) => {
  scheduledMessageTriggeredHandler = handler;
};

const emitScheduledMessageTriggered = (msg: { type?: string } | null | undefined) => {
  if (msg?.type === SCHEDULED_MESSAGE_TRIGGERED) {
    scheduledMessageTriggeredHandler?.(msg);
  }
};

const isOnChatRoute = () => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  return isPathActive(pathname, '/chat') || isPathActive(pathname, '/agentChat');
};

const toastScheduledJobFinished = (msg: {
  type?: string;
  error?: string | null;
  response?: { status?: string; jobName?: string };
} | null | undefined) => {
  if (msg?.type !== SCHEDULED_JOB_FINISHED) return;
  // Chat page already surfaces the finished job in the conversation; toast only off-route.
  if (isOnChatRoute()) return;

  const status = msg.response?.status;
  const jobName = msg.response?.jobName?.trim() || 'Scheduled job';
  const error = msg.error || 'unknown error';

  if (status === 'succeeded') {
    toast({
      variant: 'success',
      title: 'Scheduled job completed',
      description: `'${jobName}' finished successfully.`,
      duration: SCHEDULED_JOB_TOAST_DURATION_MS,
    });
    return;
  }
  if (status === 'failed') {
    toast({
      variant: 'destructive',
      title: 'Scheduled job failed',
      description: `'${jobName}' failed: ${error}`,
      duration: SCHEDULED_JOB_TOAST_DURATION_MS,
    });
    return;
  }
  if (status === 'timeout') {
    toast({
      variant: 'destructive',
      title: 'Scheduled job timed out',
      description: `'${jobName}' timed out: ${error}`,
      duration: SCHEDULED_JOB_TOAST_DURATION_MS,
    });
  }
};

export interface StreamingQueueItem {
  position: number;
  conversationId: string;
  messageID: string;
  type: string;
  userID: string[] | null;
  parentIds: string[];
  streamingResponse: any[];
}

interface CollaborationSocketState {
  connectionStatus: 'DISCONNECTED' | 'CONNECTED' | 'RECONNECTING' | 'SESSION_EXPIRED';
  // Backwards-compatible single-slot view: the most recent non-streaming
  // message. Kept for consumers (e.g. TypingIndicator) that only need "the
  // latest". Prefer `otherMessages` if you need every event in a batch.
  otherMessage: any | null;
  // Every non-streaming message in the latest RAF batch, in arrival order.
  // RAF coalescing previously dropped all but the last; consumers that handle
  // discrete events (participant changes, title updates, etc.) must iterate
  // this array.
  otherMessages: any[];
  streamingMessage: any | null;
  // Batched streaming messages for bulk processing
  streamingMessageBatch: any[];
  streamingQueue: StreamingQueueItem[];
  unsubscribeFn: (() => void) | null;
  configurationSettings: ConfigurationSettings | null;
  init: (configurationSettings: ConfigurationSettings) => void;
  reconnectWithFreshToken: () => void;
  /** Refresh JWT if needed and reopen the socket before sending a message. */
  ensureConnected: () => Promise<boolean>;
  sendMessage: (payload: any) => void;
  clearMessages: () => void;
  clearOtherMessages: () => void;
  // Atomically returns the accumulated streaming batch and empties the slot in
  // the same synchronous set(). Consumers must use this instead of reading the
  // subscribed value + clearMessages(), otherwise batches that land between a
  // render and the async clear are silently dropped.
  drainStreamingBatch: () => any[];
  clearStreamingQueue: () => void;
  removeFromStreamingQueue: (positions: number[]) => void;
}

const categorizeMessage = (msg: any): { isStreaming: boolean; isOther: boolean } => {
  const isStreaming = msg.type === 'ASSISTANT_STREAMED_RESPONSE' || msg.type === 'USER_MESSAGE';
  const isOther = !isStreaming;
  return { isStreaming, isOther };
};

export const useCollaborationSocketStore = create<CollaborationSocketState>((set, get) => ({
  connectionStatus: 'DISCONNECTED',
  otherMessage: null,
  otherMessages: [],
  streamingMessage: null,
  streamingMessageBatch: [],
  streamingQueue: [],
  unsubscribeFn: null,
  configurationSettings: null,

  init: (configurationSettings: ConfigurationSettings) => {
    // Unsubscribe from previous listener if it exists to prevent duplicates
    const currentUnsubscribe = get().unsubscribeFn;
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    const wsBase = configurationSettings.paths.seliseAiBaseUrl.replace(/^http/, 'ws');
    const url =
      `${wsBase}${configurationSettings.paths.seliseAi.collaboration}` +
      `?x-blocks-key=${encodeURIComponent(configurationSettings.blocksKey)}`;
    // Clears any terminal SESSION_EXPIRED state left by a previous session.
    socketManager.resetReconnectAttempts();
    socketManager.connect(
      url,
      () => useAuthStore.getState().accessToken ?? '',
      () => refreshAccessToken(configurationSettings),
    );

    // Store configuration settings for later use
    set({ configurationSettings });

    // Subscribe and store the unsubscribe function
    const unsubscribe = socketManager.subscribe((data: SocketMessage) => {
      // Handle connection status events immediately
      if (data.type === '__STATUS__') {
        set({ connectionStatus: data.status });
        return;
      }

      // Handle batched messages from RAF batching
      if (data.type === '__BATCH__') {
        const batchData = data as BatchedMessage;
        const messages = batchData.messages;

        if (messages.length === 0) return;

        // Keep every non-streaming message in arrival order. Earlier we only
        // surfaced the last one, which silently dropped events that arrived
        // within the same RAF tick (e.g. CONVERSATION_CREATED + ADD_PARTICIPANT
        // + CONVERSATION_TITLE_GENERATED on group creation).
        const otherMessages: any[] = [];
        const streamingMessages: any[] = [];

        for (const msg of messages) {
          emitScheduledMessageTriggered(msg);
          toastScheduledJobFinished(msg);
          const { isStreaming, isOther } = categorizeMessage(msg);

          if (isOther) {
            otherMessages.push(msg);
          }

          if (isStreaming) {
            streamingMessages.push(msg);
          }
        }

        const previousOthers = get().otherMessages;
        const mergedOthers = previousOthers.length
          ? [...previousOthers, ...otherMessages]
          : otherMessages;

        // Accumulate streaming messages the same way we accumulate `otherMessages`.
        // When many conversations stream in quick succession, multiple RAF batches
        // can land before React commits a render and runs the draining effect.
        // Replacing (rather than merging) this slot silently dropped every batch
        // but the last, so those messages never reached the chat store even though
        // they arrived on the socket.
        const previousStreaming = get().streamingMessageBatch;
        const mergedStreaming = previousStreaming.length
          ? [...previousStreaming, ...streamingMessages]
          : streamingMessages;

        // Single set() call for the entire batch - this is the key optimization
        set({
          otherMessage: otherMessages.length ? otherMessages[otherMessages.length - 1] : null,
          otherMessages: mergedOthers,
          streamingMessage: streamingMessages.length
            ? streamingMessages[streamingMessages.length - 1]
            : get().streamingMessage,
          streamingMessageBatch: mergedStreaming,
        });

        return;
      }

      // Fallback for non-batched messages (shouldn't happen with RAF batching,
      // but kept for backward compatibility during transition)
      emitScheduledMessageTriggered(data);
      toastScheduledJobFinished(data);
      const { isStreaming, isOther } = categorizeMessage(data);
      const previousOthers = get().otherMessages;
      const previousStreaming = get().streamingMessageBatch;

      set({
        otherMessage: isOther ? data : null,
        otherMessages: isOther ? [...previousOthers, data] : previousOthers,
        streamingMessage: isStreaming ? data : get().streamingMessage,
        streamingMessageBatch: isStreaming ? [...previousStreaming, data] : previousStreaming,
      });
    });

    // Store the unsubscribe function
    set({ unsubscribeFn: unsubscribe });
  },

  reconnectWithFreshToken: () => {
    const configurationSettings = get().configurationSettings;
    if (!configurationSettings) return;
    const wsBase = configurationSettings.paths.seliseAiBaseUrl.replace(/^http/, 'ws');
    const url =
      `${wsBase}${configurationSettings.paths.seliseAi.collaboration}` +
      `?x-blocks-key=${encodeURIComponent(configurationSettings.blocksKey)}`;
    socketManager.resetReconnectAttempts();
    socketManager.connect(
      url,
      () => useAuthStore.getState().accessToken ?? '',
      () => refreshAccessToken(configurationSettings),
    );
  },

  ensureConnected: async (): Promise<boolean> => {
    if (socketManager.isSocketHealthy()) {
      return true;
    }

    const configurationSettings = get().configurationSettings;
    if (!configurationSettings) return false;

    set({ connectionStatus: 'RECONNECTING' });

    try {
      await refreshAccessToken(configurationSettings);
    } catch (err) {
      // Don't reopen with a token we know is stale — let socketManager's own
      // backoff (and its auth-failure budget) decide when the session is dead.
      console.error('Token refresh before WS reconnect failed:', err);
      set({ connectionStatus: 'DISCONNECTED' });
      return false;
    }

    get().reconnectWithFreshToken();

    return new Promise((resolve) => {
      if (socketManager.isSocketHealthy()) {
        set({ connectionStatus: 'CONNECTED' });
        resolve(true);
        return;
      }

      const timeoutMs = 5000;
      const timeoutId = setTimeout(() => {
        unsubscribe();
        resolve(socketManager.isSocketHealthy());
      }, timeoutMs);

      const unsubscribe = socketManager.subscribe((data: SocketMessage) => {
        if (data.type === '__STATUS__') {
          if (data.status === 'CONNECTED') {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve(true);
          } else if (data.status === 'SESSION_EXPIRED') {
            clearTimeout(timeoutId);
            unsubscribe();
            set({ connectionStatus: 'SESSION_EXPIRED' });
            resolve(false);
          } else if (data.status === 'DISCONNECTED') {
            set({ connectionStatus: 'DISCONNECTED' });
          }
        }
      });
    });
  },

  sendMessage: (payload: any) => {
    socketManager.send(payload);
  },

  clearMessages: () =>
    // Only the streaming slots — the "other" queue is drained by its own
    // consumer (chat page effect) so we don't race against it from here.
    set({
      streamingMessage: null,
      streamingMessageBatch: [],
    }),

  clearOtherMessages: () =>
    set({
      otherMessage: null,
      otherMessages: [],
    }),

  drainStreamingBatch: () => {
    const batch = get().streamingMessageBatch;
    if (batch.length === 0) return [];
    set({
      streamingMessage: null,
      streamingMessageBatch: [],
    });
    return batch;
  },

  clearStreamingQueue: () =>
    set({
      streamingQueue: [],
    }),

  removeFromStreamingQueue: (positions: number[]) =>
    set((state) => {
      const currentQueue = [...state.streamingQueue];
      const filteredQueue = currentQueue.filter((item) => !positions.includes(item.position));
      return { streamingQueue: filteredQueue };
    }),
}));
